/**
 * Adapter Google Places, Geocoding e Maps Static.
 * Chave via VITE_GOOGLE_MAPS_API_KEY.
 */
import type { PlaceResult } from './locationPlaces';

type GooglePlace = {
    id?: string;
    displayName?: string | { text?: string };
    formattedAddress?: string;
    location?: { latitude?: number; longitude?: number; lat?: number; lng?: number };
};

type GeocodeResult = {
    place_id?: string;
    formatted_address?: string;
    types?: string[];
    address_components?: Array<{ long_name?: string; types?: string[] }>;
    geometry?: { location?: { lat?: number; lng?: number } };
};

const NEARBY_TYPES = [
    'church',
    'restaurant',
    'pharmacy',
    'supermarket',
    'bakery',
    'school',
    'bank',
    'store',
] as const;

const PLACE_FIELD_MASK =
    'places.id,places.displayName,places.formattedAddress,places.location';

export function getGoogleMapsApiKey(): string {
    const key = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    return typeof key === 'string' ? key.trim() : '';
}

export function hasGooglePlacesSearch(): boolean {
    return getGoogleMapsApiKey().length > 0;
}

function requireApiKey(): string {
    const apiKey = getGoogleMapsApiKey();
    if (!apiKey) throw new Error('Google Places: configure VITE_GOOGLE_MAPS_API_KEY');
    return apiKey;
}

function readDisplayName(value: GooglePlace['displayName']): string {
    if (!value) return '';
    return typeof value === 'string' ? value.trim() : (value.text || '').trim();
}

function readLocation(location: GooglePlace['location']): { lat: number; lng: number } | null {
    if (!location) return null;
    const lat = location.latitude ?? location.lat;
    const lng = location.longitude ?? location.lng;
    if (typeof lat !== 'number' || typeof lng !== 'number') return null;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return { lat, lng };
}

function mapGooglePlaces(places: GooglePlace[] | undefined, idPrefix: string): PlaceResult[] {
    const mapped: PlaceResult[] = [];
    const seen = new Set<string>();
    for (const item of places || []) {
        const coords = readLocation(item.location);
        const name = readDisplayName(item.displayName);
        if (!coords || !name) continue;
        const id = `${idPrefix}-${item.id ?? `${coords.lat},${coords.lng}`}`;
        if (seen.has(id)) continue;
        seen.add(id);
        mapped.push({
            id,
            name,
            address: (item.formattedAddress || '').trim() || `${coords.lat}, ${coords.lng}`,
            latitude: coords.lat,
            longitude: coords.lng,
        });
    }
    return mapped;
}

async function fetchJson<T>(
    url: string,
    init: RequestInit,
    timeoutMs: number,
    errorLabel: string
): Promise<T> {
    const controller = new AbortController();
    const timer = globalThis.setTimeout(() => controller.abort(), timeoutMs);
    try {
        const res = await fetch(url, { ...init, signal: controller.signal });
        if (!res.ok) {
            const body = await res.text().catch(() => '');
            throw new Error(`${errorLabel} (${res.status}): ${body.slice(0, 180)}`);
        }
        return (await res.json()) as T;
    } finally {
        globalThis.clearTimeout(timer);
    }
}

async function postPlaces<T>(
    path: 'places:searchText' | 'places:searchNearby',
    body: Record<string, unknown>
): Promise<T> {
    return fetchJson<T>(
        `https://places.googleapis.com/v1/${path}`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Goog-Api-Key': requireApiKey(),
                'X-Goog-FieldMask': PLACE_FIELD_MASK,
            },
            body: JSON.stringify(body),
        },
        12000,
        'Google Places falhou'
    );
}

/** Text Search (New) — empresa/loja em qualquer cidade. */
export async function searchGooglePlaces(query: string): Promise<PlaceResult[]> {
    const q = query.trim();
    if (q.length < 2) return [];
    const data = await postPlaces<{ places?: GooglePlace[] }>('places:searchText', {
        textQuery: q,
        languageCode: 'pt-BR',
        regionCode: 'BR',
        maxResultCount: 15,
    });
    return mapGooglePlaces(data.places, 'gplace');
}

/** Nearby Search (New) — POIs em torno do GPS. */
export async function searchGoogleNearbyPlaces(near: {
    latitude: number;
    longitude: number;
}): Promise<PlaceResult[]> {
    const data = await postPlaces<{ places?: GooglePlace[] }>('places:searchNearby', {
        languageCode: 'pt-BR',
        regionCode: 'BR',
        maxResultCount: 20,
        rankPreference: 'DISTANCE',
        includedTypes: [...NEARBY_TYPES],
        locationRestriction: {
            circle: {
                center: { latitude: near.latitude, longitude: near.longitude },
                radius: 2000.0,
            },
        },
    });
    return mapGooglePlaces(data.places, 'gnear');
}

/** Prefere endereço de via/número a estabelecimento (evita cidade vizinha em fronteira). */
function pickBestGeocodeResult(results: GeocodeResult[] | undefined): GeocodeResult | undefined {
    if (!results?.length) return undefined;
    const preferred = [
        'street_address',
        'premise',
        'subpremise',
        'route',
        'intersection',
    ];
    return (
        results.find((r) => r.types?.some((t) => preferred.includes(t))) ??
        results.find((r) => !r.types?.includes('establishment')) ??
        results[0]
    );
}

/** Reverse geocode via Geocoding API (mesmo key do Maps Platform). */
export async function reverseGeocodeGoogle(
    latitude: number,
    longitude: number
): Promise<PlaceResult> {
    const params = new URLSearchParams({
        latlng: `${latitude},${longitude}`,
        language: 'pt-BR',
        key: requireApiKey(),
    });
    const data = await fetchJson<{
        status?: string;
        results?: GeocodeResult[];
        error_message?: string;
    }>(
        `https://maps.googleapis.com/maps/api/geocode/json?${params}`,
        {},
        10000,
        'Google Geocoding falhou'
    );

    if (data.status && data.status !== 'OK' && data.status !== 'ZERO_RESULTS') {
        throw new Error(
            `Google Geocoding falhou (${data.status}): ${data.error_message || ''}`.trim()
        );
    }

    const best = pickBestGeocodeResult(data.results);
    const address = best?.formatted_address?.trim();
    if (!best || !address) {
        throw new Error('Endereço não encontrado para estas coordenadas.');
    }

    const route = best.address_components?.find((c) => c.types?.includes('route'))?.long_name;
    const locality = best.address_components?.find((c) =>
        c.types?.some((t) => ['sublocality', 'neighborhood', 'locality'].includes(t))
    )?.long_name;

    return {
        id: `ggeo-${best.place_id ?? `${latitude},${longitude}`}`,
        name: (route || locality || 'Minha localização').trim(),
        address,
        // Sempre as coordenadas do GPS — não as do resultado geocode (podem deslocar)
        latitude,
        longitude,
    };
}

/** Preview do ponto via Maps Static API. Sem chave, string vazia. */
export function buildStaticMapUrl(
    latitude: number,
    longitude: number,
    width = 222,
    height = 150
): string {
    const key = getGoogleMapsApiKey();
    if (!key || !Number.isFinite(latitude) || !Number.isFinite(longitude)) return '';
    const side = (value: number) => Math.min(640, Math.max(1, Math.round(value)));
    const size = `${side(width)}x${side(height)}`;
    const params = new URLSearchParams({
        center: `${latitude},${longitude}`,
        zoom: '16',
        size,
        scale: '2',
        maptype: 'roadmap',
        markers: `${latitude},${longitude}`,
        key,
    });
    return `https://maps.googleapis.com/maps/api/staticmap?${params}`;
}
